import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { Luggage, Mail, Lock, ArrowRight, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
    }
    fetchLogo();
  }, [navigate]);

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      if (response.data.logo_url) {
        setLogoUrl(`${API}/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.log("No logo configured");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password
        });

        localStorage.setItem("admin_token", response.data.token);
        localStorage.setItem("admin_info", JSON.stringify(response.data.admin));
        toast.success("Welcome back!");
        navigate("/admin/dashboard");
      } else {
        await axios.post(`${API}/admin/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name
        });

        toast.success("Account created! Please login.");
        setIsLogin(true);
        setFormData({ ...formData, password: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#004E64] flex items-center justify-center px-6" data-testid="admin-login-page">
      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector variant="dark" />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-3 mb-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Bari Bird Logo" className="h-20 w-auto object-contain" />
            ) : (
              <Luggage className="w-16 h-16 text-[#FFB400]" />
            )}
            <span className="font-serif text-xl font-semibold text-white">Bari Bird Luggage Storage</span>
          </div>
          <p className="text-white/70">{t('admin.login.portal')}</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="font-serif text-2xl font-semibold text-[#004E64]">
              {isLogin ? t('admin.login.title') : t('admin.login.createTitle')}
            </h1>
            <p className="text-[#5A6B7C] mt-2">
              {isLogin ? t('admin.login.subtitle') : t('admin.login.createSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">
                  {t('admin.login.fullName')}
                </Label>
                <Input
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg"
                  placeholder={t('admin.login.fullName')}
                  data-testid="input-name"
                />
              </div>
            )}

            <div>
              <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">
                {t('admin.login.email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#5A6B7C]" />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg pl-12"
                  placeholder="admin@example.com"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">
                {t('admin.login.password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#5A6B7C]" />
                <Input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg pl-12"
                  placeholder="••••••••"
                  data-testid="input-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-14 bg-[#004E64] hover:bg-[#003848] text-white font-bold text-lg rounded-xl shadow-lg transition-all disabled:opacity-50"
              data-testid="btn-submit"
            >
              {isLoading ? (
                <>
                  <div className="spinner mr-3 border-white/20 border-t-white"></div>
                  {isLogin ? t('admin.login.signingIn') : t('admin.login.creating')}
                </>
              ) : (
                <>
                  {isLogin ? t('admin.login.signIn') : t('admin.login.createAccount')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#004E64] hover:text-[#FFB400] font-medium transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? t('admin.login.needAccount') : t('admin.login.haveAccount')}
            </button>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white font-medium transition-colors" data-testid="back-home-link">
            <Home className="w-5 h-5" />
            {t('admin.login.backToWebsite')}
          </Link>
        </div>
      </div>
    </div>
  );
}
